import { Asset, Transaction, Liability, MonthlyExpense, AssetClassMeta, MoneyTransaction } from '../types';

// ─── Asset ────────────────────────────────────────────────────────────────────
export function getAssetCurrentValue(a: Asset) { return a.currentPrice * a.quantity; }
export function getAssetCostBasis(a: Asset) { return a.costPrice * a.quantity; }
export function getAssetUnrealizedGain(a: Asset) { return getAssetCurrentValue(a) - getAssetCostBasis(a); }
export function getAssetUnrealizedGainPercent(a: Asset) {
  const c = getAssetCostBasis(a); return c === 0 ? 0 : (getAssetUnrealizedGain(a) / c) * 100;
}

// ─── Ledger — nguồn sự thật từ giao dịch ────────────────────────────────────

/**
 * Tính giá vốn từ sổ giao dịch:
 * = SUM(tx.net_amount WHERE to_node=asset) − SUM(tx.net_amount WHERE from_node=asset AND type=SELL)
 */
export function getAssetCostBasisFromLedger(assetId: string, txs: MoneyTransaction[]): number {
  let cost = 0;
  for (const tx of txs) {
    const flowsIn = tx.to_node
      ? (tx.to_node.kind === 'asset' && tx.to_node.asset_id === assetId)
      : (tx.asset_id === assetId && ['BUY', 'DEPOSIT'].includes(tx.type));

    const sellsOut = tx.from_node
      ? (tx.from_node.kind === 'asset' && tx.from_node.asset_id === assetId && tx.type === 'SELL')
      : (tx.asset_id === assetId && tx.type === 'SELL');

    if (flowsIn)   cost += tx.net_amount;
    if (sellsOut)  cost -= tx.net_amount;
  }
  return Math.max(0, cost);
}

/** ID ví chung toàn hệ thống — tất cả giao dịch cash đều qua đây (trừ TRANSFER per-fund) */
export const GLOBAL_WALLET_ID = '__global__';

/** ID tài sản đặc biệt đại diện cho ví tiền mặt chung — được quản lý trực tiếp bởi giao dịch */
export const CASH_WALLET_ASSET_ID = '__cash_wallet__';

/**
 * Số dư ví tiền mặt.
 * - fundId === GLOBAL_WALLET_ID → tổng hợp toàn bộ dòng tiền (mọi quỹ đều gộp vào)
 * - fundId cụ thể → chỉ tính ví của quỹ đó (dùng cho TRANSFER per-fund)
 */
export function computeWalletBalance(fundId: string, txs: MoneyTransaction[]): number {
  const isGlobal = fundId === GLOBAL_WALLET_ID;
  let bal = 0;
  for (const tx of txs) {
    const toWallet = tx.to_node
      ? (tx.to_node.kind === 'wallet' && (isGlobal || tx.to_node.fund_id === fundId))
      : (isGlobal
          ? ['SELL', 'INCOME', 'DEPOSIT'].includes(tx.type)           // global: mọi inflow
          : (['SELL', 'INCOME', 'DEPOSIT'].includes(tx.type) && tx.fund_id === fundId)
            || (tx.type === 'TRANSFER' && tx.dest_fund_id === fundId));

    const fromWallet = tx.from_node
      ? (tx.from_node.kind === 'wallet' && (isGlobal || tx.from_node.fund_id === fundId))
      : (isGlobal
          ? ['BUY', 'WITHDRAW'].includes(tx.type)                     // global: mọi outflow
          : (['BUY', 'WITHDRAW'].includes(tx.type) && tx.fund_id === fundId)
            || (tx.type === 'TRANSFER' && tx.fund_id === fundId));

    if (toWallet)   bal += tx.net_amount;
    if (fromWallet) bal -= tx.net_amount;
  }
  return bal;
}

/** Tổng vốn đã nạp (DEPOSIT − WITHDRAW) cho toàn danh mục hoặc một quỹ cụ thể */
export function computeTotalCapitalInjected(txs: MoneyTransaction[], fundId?: string): number {
  return txs.reduce((s, t) => {
    if (fundId && t.fund_id !== fundId) return s;
    if (t.type === 'DEPOSIT')  return s + t.net_amount;
    if (t.type === 'WITHDRAW') return s - t.net_amount;
    return s;
  }, 0);
}

// ── Dữ liệu biểu đồ cho 2 báo cáo ─────────────────────────────────────────

function getMonthKeys(monthsBack: number): string[] {
  const keys: string[] = [];
  for (let i = monthsBack - 1; i >= 0; i--) {
    const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - i);
    keys.push(d.toISOString().slice(0, 7));
  }
  return keys;
}

function shortMonthLabel(mk: string) {
  const [y, m] = mk.split('-');
  return `T${Number(m)}/${y.slice(2)}`;
}

export interface CapitalFlowPoint {
  month: string;
  vonNap: number;         // DEPOSIT (vốn mới từ bên ngoài)
  rutVon: number;         // WITHDRAW
  trienkhai: number;      // BUY (triển khai vào tài sản)
  thuVe: number;          // SELL + INCOME (tiền về ví)
  thuNhap: number;        // INCOME riêng (yield thuần)
}

/** Report 1: Dòng vốn đầu tư theo tháng */
export function buildCapitalFlowSeries(txs: MoneyTransaction[], monthsBack = 12): CapitalFlowPoint[] {
  return getMonthKeys(monthsBack).map(mk => {
    const p = txs.filter(t => t.date.startsWith(mk));
    return {
      month:      shortMonthLabel(mk),
      vonNap:     p.filter(t => t.type === 'DEPOSIT').reduce((s, t) => s + t.net_amount, 0),
      rutVon:     p.filter(t => t.type === 'WITHDRAW').reduce((s, t) => s + t.net_amount, 0),
      trienkhai:  p.filter(t => t.type === 'BUY').reduce((s, t) => s + t.net_amount, 0),
      thuVe:      p.filter(t => ['SELL', 'INCOME'].includes(t.type)).reduce((s, t) => s + t.net_amount, 0),
      thuNhap:    p.filter(t => t.type === 'INCOME').reduce((s, t) => s + t.net_amount, 0),
    };
  });
}

export interface CashFlowRealPoint {
  month: string;
  vonNgoai: number;   // DEPOSIT — vốn từ bên ngoài (lương, thưởng)
  rutRa: number;      // WITHDRAW — rút ra ngoài (chi tiêu)
  yield_: number;     // INCOME — thu nhập từ tài sản (cổ tức, lãi, tiền thuê)
  realized: number;   // SELL — dòng tiền thực khi bán tài sản
}

/** Report 2: Dòng tiền thực — external vs yield */
export function buildCashFlowRealSeries(txs: MoneyTransaction[], monthsBack = 12): CashFlowRealPoint[] {
  return getMonthKeys(monthsBack).map(mk => {
    const p = txs.filter(t => t.date.startsWith(mk));
    return {
      month:    shortMonthLabel(mk),
      vonNgoai: p.filter(t => t.type === 'DEPOSIT').reduce((s, t) => s + t.net_amount, 0),
      rutRa:    p.filter(t => t.type === 'WITHDRAW').reduce((s, t) => s + t.net_amount, 0),
      yield_:   p.filter(t => t.type === 'INCOME').reduce((s, t) => s + t.net_amount, 0),
      realized: p.filter(t => t.type === 'SELL').reduce((s, t) => s + t.net_amount, 0),
    };
  });
}

// ─── Portfolio ────────────────────────────────────────────────────────────────
export function getTotalNAV(assets: Asset[]) {
  return assets.reduce((s, a) => s + getAssetCurrentValue(a), 0);
}
export function getTotalCost(assets: Asset[]) {
  return assets.reduce((s, a) => s + getAssetCostBasis(a), 0);
}
export function getTotalRealizedGain(txs: Transaction[]) {
  return txs.reduce((s, t) => s + (t.realizedGain || 0), 0);
}

// ─── Liabilities ─────────────────────────────────────────────────────────────
export function getTotalLiabilities(liabilities: Liability[]) {
  return liabilities.reduce((s, l) => s + l.outstandingBalance, 0);
}
export function getNetWorth(assets: Asset[], liabilities: Liability[]) {
  return getTotalNAV(assets) - getTotalLiabilities(liabilities);
}
export function getTotalMonthlyDebtPayment(liabilities: Liability[]) {
  return liabilities.reduce((s, l) => s + l.monthlyPayment, 0);
}

// ─── Bucket ───────────────────────────────────────────────────────────────────
export function getBucketNAV(bucketId: string, assets: Asset[]) {
  return assets.filter(a => a.bucketId === bucketId)
    .reduce((s, a) => s + getAssetCurrentValue(a), 0);
}
/** Returns actual value as % of total portfolio (for display purposes only) */
export function getBucketActualAllocation(bucketId: string, assets: Asset[]) {
  const total = getTotalNAV(assets);
  return total === 0 ? 0 : (getBucketNAV(bucketId, assets) / total) * 100;
}
/** Returns progress toward absolute targetValue (0–Infinity, capped display elsewhere) */
export function getBucketProgress(bucket: import('../types').Bucket, assets: Asset[]) {
  const nav = getBucketNAV(bucket.id, assets);
  const pct = bucket.targetValue > 0 ? (nav / bucket.targetValue) * 100 : null;
  const diff = bucket.targetValue > 0 ? nav - bucket.targetValue : null;
  return { nav, pct, diff };
}

// ─── Asset class breakdown ────────────────────────────────────────────────────
// Fallback khi chưa có master data — match cả theo name lẫn id
const FALLBACK_COLORS: Record<string, string> = {
  // by name (legacy)
  'BĐS': '#10b981', 'Bất động sản': '#10b981',
  'Cổ phiếu': '#38bdf8',
  'Thu nhập cố định': '#fbbf24', 'Trái phiếu': '#f59e0b',
  'Tiền mặt': '#a78bfa',
  'ESOP': '#8b5cf6',
  'Tiền điện tử': '#f7931a', 'Vàng vật chất': '#fbbf24',
  'Tài sản tiêu dùng': '#94a3b8', 'Khác': '#94a3b8',
  // by id
  'bds': '#10b981', 'real-estate': '#10b981',
  'stocks': '#38bdf8',
  'fixed': '#fbbf24', 'bonds': '#f59e0b',
  'cash': '#a78bfa',
  'esop': '#8b5cf6',
  'crypto': '#f7931a',
  'gold': '#fbbf24',
  'consumer': '#94a3b8',
  'other': '#94a3b8',
};

/** Tra màu từ master data (match theo name hoặc id); fallback nếu không tìm thấy */
export function resolveClassColor(nameOrId: string, assetClasses?: AssetClassMeta[]): string {
  if (assetClasses && assetClasses.length > 0) {
    const found = assetClasses.find(c => c.name === nameOrId || c.id === nameOrId);
    if (found) return found.color;
  }
  return FALLBACK_COLORS[nameOrId] || '#94a3b8';
}

/** @deprecated dùng resolveClassColor(name, assetClasses) để lấy màu từ master data */
export function assetClassColor(cls: string) { return FALLBACK_COLORS[cls] || '#94a3b8'; }

export function getAssetClassBreakdown(assets: Asset[], assetClasses?: AssetClassMeta[]) {
  // Key by assetClassId when available for accurate grouping after class changes
  const map: Record<string, { name: string; value: number }> = {};
  assets.forEach(a => {
    const key = a.assetClassId || a.assetClass || 'Khác';
    const name = assetClasses?.find(c => c.id === key)?.name ?? a.assetClass ?? key;
    if (!map[key]) map[key] = { name, value: 0 };
    map[key].value += getAssetCurrentValue(a);
  });
  return Object.entries(map).map(([key, { name, value }]) => ({
    name, value,
    color: resolveClassColor(key, assetClasses) || resolveClassColor(name, assetClasses),
  }));
}

// ─── Cash Flow Projection ────────────────────────────────────────────────────

export interface IncomeItem {
  assetId: string;
  assetCode: string;
  assetName: string;
  amount: number;
  label: string;
}

export interface DebtItem {
  liabilityId: string;
  name: string;
  amount: number;
}

export interface MaturityItem {
  assetId: string;
  assetCode: string;
  amount: number;
  label: string;
}

export interface MonthlyProjection {
  year: number;
  month: number;       // 1–12
  label: string;       // "T4/2025"
  labelShort: string;  // "T4"

  incomeItems: IncomeItem[];
  totalIncome: number;

  plannedExpense: number;    // chi phí sinh hoạt kế hoạch
  actualExpense?: number;    // chi phí thực tế ghi nhận
  totalDebtPayment: number;
  debtItems: DebtItem[];
  totalExpense: number;      // expense + debt

  maturityItems: MaturityItem[];
  totalMaturity: number;

  net: number;               // totalIncome - totalExpense
  cumulativeNet: number;     // running cumulative
}

export interface CashFlowOptions {
  /** Tháng bắt đầu (1–12) */
  startMonth: number;
  /** Năm bắt đầu */
  startYear: number;
  /** Số tháng chiếu */
  months: number;
  /** Nếu có, chỉ tính tài sản thuộc các bucket này */
  bucketFilter?: string[];
  /** Chi phí sinh hoạt mặc định mỗi tháng */
  plannedExpense: number;
  /** Chi phí thực tế đã ghi nhận */
  monthlyExpenses: MonthlyExpense[];
}

/**
 * Tính lịch dòng tiền cho 1 asset trong 1 tháng cụ thể.
 * Ưu tiên incomeEvents (nếu có), fallback monthlyIncome/incomeFrequency.
 */
function getAssetIncomeForMonth(asset: Asset, year: number, month: number): IncomeItem[] {
  const items: IncomeItem[] = [];

  if (asset.incomeEvents && asset.incomeEvents.length > 0) {
    asset.incomeEvents
      .filter(ev => ev.month === month)
      .forEach(ev => {
        items.push({
          assetId: asset.id, assetCode: asset.code,
          assetName: asset.name, amount: ev.amount, label: ev.label,
        });
      });
    return items;
  }

  // Legacy fallback
  if (!asset.monthlyIncome || asset.monthlyIncome === 0) return [];
  const mi = asset.monthlyIncome;
  const freq = asset.incomeFrequency;

  if (freq === 'monthly') {
    items.push({ assetId: asset.id, assetCode: asset.code, assetName: asset.name, amount: mi, label: `${asset.code} T${month}` });
  } else if (freq === 'quarterly' && month % 3 === 0) {
    items.push({ assetId: asset.id, assetCode: asset.code, assetName: asset.name, amount: mi * 3, label: `${asset.code} quý` });
  } else if (freq === 'annual' && month === 12) {
    items.push({ assetId: asset.id, assetCode: asset.code, assetName: asset.name, amount: mi * 12, label: `${asset.code} năm` });
  }
  return items;
}

export function buildCashFlowProjection(
  assets: Asset[],
  liabilities: Liability[],
  options: CashFlowOptions,
): MonthlyProjection[] {
  const { startMonth, startYear, months, bucketFilter, plannedExpense, monthlyExpenses } = options;

  // Filter assets by bucket if needed
  const relevantAssets = bucketFilter && bucketFilter.length > 0
    ? assets.filter(a => bucketFilter.includes(a.bucketId))
    : assets;

  const result: MonthlyProjection[] = [];
  let cumulative = 0;

  for (let i = 0; i < months; i++) {
    const totalMonths = (startYear - 2000) * 12 + (startMonth - 1) + i;
    const year = 2000 + Math.floor(totalMonths / 12);
    const month = (totalMonths % 12) + 1;
    const label = `T${month}/${year}`;
    const labelShort = `T${month}`;

    // ── Income ──
    const incomeItems: IncomeItem[] = [];
    relevantAssets.forEach(a => {
      incomeItems.push(...getAssetIncomeForMonth(a, year, month));
    });
    const totalIncome = incomeItems.reduce((s, x) => s + x.amount, 0);

    // ── Expenses ──
    const expenseRecord = monthlyExpenses.find(e => e.year === year && e.month === month);
    const actualExpense = expenseRecord?.amount;

    // ── Debt payments ──
    const debtItems: DebtItem[] = liabilities
      .filter(l => {
        const start = new Date(l.startDate);
        const end = new Date(l.maturityDate);
        const d = new Date(year, month - 1, 1);
        return d >= start && d <= end;
      })
      .map(l => ({ liabilityId: l.id, name: l.name, amount: l.monthlyPayment }));
    const totalDebtPayment = debtItems.reduce((s, d) => s + d.amount, 0);

    const effectiveExpense = actualExpense !== undefined ? actualExpense : plannedExpense;
    const totalExpense = effectiveExpense + totalDebtPayment;

    // ── Maturity ──
    const maturityItems: MaturityItem[] = [];
    assets.forEach(a => {
      if (!a.maturityDate) return;
      const md = new Date(a.maturityDate);
      if (md.getFullYear() === year && md.getMonth() + 1 === month) {
        maturityItems.push({ assetId: a.id, assetCode: a.code, amount: a.currentPrice * a.quantity, label: `Đáo hạn ${a.code}` });
      }
    });
    const totalMaturity = maturityItems.reduce((s, m) => s + m.amount, 0);

    const net = totalIncome - totalExpense;
    cumulative += net;

    result.push({
      year, month, label, labelShort,
      incomeItems, totalIncome,
      plannedExpense, actualExpense,
      totalDebtPayment, debtItems,
      totalExpense,
      maturityItems, totalMaturity,
      net, cumulativeNet: cumulative,
    });
  }

  return result;
}

/** Tổng thu nhập hàng năm — ưu tiên: estimatedIncome > incomeEvents > legacy */
export function getAssetAnnualIncome(asset: Asset): number {
  if ((asset.estimatedIncome ?? 0) > 0) return asset.estimatedIncome!;
  if (asset.incomeEvents && asset.incomeEvents.length > 0) {
    return asset.incomeEvents.reduce((s, e) => s + e.amount, 0);
  }
  if (!asset.monthlyIncome) return 0;
  if (asset.incomeFrequency === 'monthly') return asset.monthlyIncome * 12;
  if (asset.incomeFrequency === 'quarterly') return asset.monthlyIncome * 4;
  if (asset.incomeFrequency === 'annual') return asset.monthlyIncome;
  return 0;
}

export function getTotalAnnualIncome(assets: Asset[]): number {
  return assets.reduce((s, a) => s + getAssetAnnualIncome(a), 0);
}

/** Tổng thu nhập tăng giá vốn hàng năm dự phóng = currentValue * capitalGainRate% */
export function getAssetCapitalGainIncome(asset: Asset): number {
  return getAssetCurrentValue(asset) * (asset.capitalGainRate ?? 0) / 100;
}
